import { IEntity } from '../../types'
import { generateFactoryFromSeed, hashTile } from './factoryGenerator'
import type { FactoryGenerationResult } from './factoryGenerator'

export const TILE_SIZE = 16 // tiles per grid
export const TILE_GAP = 4 // gap between grids in tiles
export const TILE_STRIDE = TILE_SIZE + TILE_GAP // total spacing between tile origins

/** Cache entry for a generated tile */
interface TileCache {
    entities: IEntity[]
    seed: bigint
}

/**
 * TileWorld manages an infinite 2D grid of deterministic factory tiles.
 * Each tile at (tx, ty) produces a unique factory based on hashed coordinates.
 * Only the visible window of tiles is kept in memory.
 */
export class TileWorld {
    /** Currently visible tile extent (inclusive) */
    public minTX = -1
    public maxTX = 1
    public minTY = -1
    public maxTY = 1

    private cache = new Map<string, TileCache>()

    /** Generate (or retrieve from cache) the factory for a tile */
    getTile(tx: number, ty: number): TileCache {
        const key = `${tx},${ty}`
        let cached = this.cache.get(key)
        if (!cached) {
            const result = generateFactoryFromSeed(hashTile(tx, ty))
            cached = { entities: result.entities, seed: result.seed }
            this.cache.set(key, cached)
        }
        return cached
    }

    /** Get all entities for currently visible tiles, with positions offset to world space */
    getVisibleEntities(): IEntity[] {
        const allEntities: IEntity[] = []
        let entityNumber = 1

        for (let ty = this.minTY; ty <= this.maxTY; ty++) {
            for (let tx = this.minTX; tx <= this.maxTX; tx++) {
                const tile = this.getTile(tx, ty)
                const offsetX = tx * TILE_STRIDE
                const offsetY = ty * TILE_STRIDE

                for (const e of tile.entities) {
                    allEntities.push({
                        ...e,
                        entity_number: entityNumber++,
                        position: {
                            x: e.position.x + offsetX,
                            y: e.position.y + offsetY,
                        },
                    })
                }
            }
        }

        return allEntities
    }

    /** Get a single factory for the tile at (tx, ty), used as the "active" tile for the panel */
    getActiveTile(tx: number, ty: number): FactoryGenerationResult {
        const tile = this.getTile(tx, ty)
        return { entities: tile.entities, seed: tile.seed }
    }

    /** Update the visible tile window based on a center tile coordinate */
    setCenter(tx: number, ty: number, radius: number = 1): boolean {
        const newMinTX = tx - radius
        const newMaxTX = tx + radius
        const newMinTY = ty - radius
        const newMaxTY = ty + radius

        if (
            newMinTX === this.minTX &&
            newMaxTX === this.maxTX &&
            newMinTY === this.minTY &&
            newMaxTY === this.maxTY
        ) {
            return false // no change
        }

        // Prune tiles that are now outside the visible window
        for (const [key] of this.cache) {
            const [sx, sy] = key.split(',').map(Number)
            if (sx < newMinTX || sx > newMaxTX || sy < newMinTY || sy > newMaxTY) {
                this.cache.delete(key)
            }
        }

        this.minTX = newMinTX
        this.maxTX = newMaxTX
        this.minTY = newMinTY
        this.maxTY = newMaxTY
        return true
    }

    /** Convert world-space PIXEL position to tile grid coordinate */
    static worldToTile(pixelX: number, pixelY: number): { tx: number; ty: number } {
        return {
            tx: Math.floor((pixelX / 32) / TILE_STRIDE),
            ty: Math.floor((pixelY / 32) / TILE_STRIDE),
        }
    }

    /** Tile coordinate to world-space center pixel position */
    static tileToWorld(tx: number, ty: number): { x: number; y: number } {
        return {
            x: tx * TILE_STRIDE + TILE_SIZE / 2,
            y: ty * TILE_STRIDE + TILE_SIZE / 2,
        }
    }
}
